import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

// Every outcome bucket services/gmail_amount_reextraction.py can tally,
// Preview ("would_...") and Apply (actual outcome) side by side -- see that
// module's docstring for the full sub-cohort x outcome naming table.
const BUCKET_LABELS = [
  ['resolved_from_pdf', 'would_resolve_from_pdf', 'Resolved from PDF'],
  ['quarantined_from_pdf', 'would_quarantine_from_pdf', 'Quarantined from PDF'],
  ['unfetchable_from_pdf', 'would_be_unfetchable_from_pdf', 'Unfetchable from PDF'],
  ['resolved_from_gmail', 'would_resolve_from_gmail', 'Resolved from Gmail'],
  ['quarantined_with_pdf', 'would_quarantine_with_pdf', 'Quarantined (PDF recovered)'],
  ['duplicate_of_existing', 'would_duplicate_of_existing', 'Duplicate notification (sibling has the amount)'],
  ['gmail_source_gone', 'gmail_source_gone', 'Gmail source gone'],
  ['fetch_error', 'fetch_error', 'Fetch error'],
  ['skipped_conflict', 'skipped_conflict', 'Skipped (conflict)'],
];

// Mirrors DrivePathAuditPanel.jsx's useJobPolling, parameterised by which
// job id to poll (same reasoning as RevolutCounterpartyBackfillSection.jsx):
// independent poll/elapsed refs per operation so recovering/polling Preview
// can never stop Apply's poll, or vice versa.
function useJobPolling({ onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.gmailAmountReextractionJobStatus(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress]);

  return { polling, elapsed, start };
}

function ChainSection({
  operation, title, icon, chain, progress, failed, polling, elapsed, starting, stopping,
  onRun, onStop, runLabel, runDisabled,
}) {
  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;
  const tallies = live?.tallies || {};
  const errorText = (chain?.status === 'error' && chain?.error) || failed;

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-1">{icon}{title}</span>
        <Button data-testid={`gmail-reextraction-${operation}-run`} onClick={onRun} disabled={runDisabled} size="sm">
          {(starting || polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
            : runLabel}
        </Button>
        {running && (
          <Button data-testid={`gmail-reextraction-${operation}-stop`} onClick={onStop} disabled={stopping} size="sm" variant="outline">
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>

      {chain && (
        <div className="text-xs space-y-1" data-testid={`gmail-reextraction-${operation}-chain-summary`}>
          <div className="flex items-center gap-1">
            <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
              {chain.status}
            </Badge>
            <span className="text-muted-foreground">
              run {fmtInt(chain.runs_count)}
              {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} processed</>}
            </span>
            {stalled && (
              <span className="text-amber-600 dark:text-amber-400">
                last active {heartbeatAge}s ago -- watchdog will resume it
              </span>
            )}
          </div>

          {Object.keys(tallies).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid={`gmail-reextraction-${operation}-tallies`}>
              {BUCKET_LABELS.map(([applyKey, previewKey, label]) => {
                const key = operation === 'apply' ? applyKey : previewKey;
                const count = tallies[key];
                if (!count) return null;
                return (
                  <span key={key} className={key === 'fetch_error' ? 'text-red-600' : ''}>
                    {label}: <strong>{count}</strong>
                  </span>
                );
              })}
              {tallies.stale_filename_count > 0 && (
                <span>Stale-amount filenames: <strong>{tallies.stale_filename_count}</strong></span>
              )}
            </div>
          )}

          {errorText && <p className="text-red-600 dark:text-red-400">{errorText}</p>}
        </div>
      )}
    </div>
  );
}

export function GmailAmountReextractionPanel() {
  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailAmountReextractionPreviewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailAmountReextractionApplyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  // Recover in-flight/last-finished job on mount for each operation
  // independently, mirroring DrivePathAuditPanel.jsx.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailAmountReextractionJobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailAmountReextractionJobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);

  // Keep polling chain status while running, including the gap between one
  // run finishing and its successor starting (when the job poll above is
  // briefly idle -- no job is "running" to poll in that window).
  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.gmailAmountReextractionPreviewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Re-run extraction against ${cohortCount ?? 'the'} Gmail invoice(s) with amount 0/missing? ` +
      'Each resolves to a corrected amount or is routed to quarantine -- never left as a silent 0.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.gmailAmountReextractionApplyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await diagnosticsAPI.gmailAmountReextractionPreviewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await diagnosticsAPI.gmailAmountReextractionApplyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  const previewComplete = previewChain?.status === 'complete';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Gmail Amount Re-extraction</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Gmail invoices ingested before the amount-quarantine gate fix (CLAUDE.md
          Prompt 7) had a failed AI extraction silently stored as amount 0 instead of
          quarantining. Both Preview and Apply are budget-boxed chains: each run
          time-boxes itself and schedules its own successor from the persisted
          cursor/tallies until the cohort converges, so a large cohort no longer 504s.
          Docs with no Drive file at all (metadata-only stubs) are recovered from the
          original Gmail attachment instead. Apply is gated behind a completed Preview
          over the same cohort -- if the cohort changed since Preview finished, re-run
          Preview first. Does not rename Drive files; stale-amount filenames are
          counted, not corrected.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ChainSection
          operation="preview"
          title="Preview"
          icon={<Eye className="h-3 w-3" />}
          chain={previewChain}
          progress={previewProgress}
          failed={previewFailed}
          polling={previewPoll.polling}
          elapsed={previewPoll.elapsed}
          starting={previewStarting}
          stopping={previewStopping}
          onRun={runPreview}
          onStop={stopPreviewChain}
          runLabel="Preview"
          runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
        />
        <ChainSection
          operation="apply"
          title="Apply"
          icon={<Play className="h-3 w-3" />}
          chain={applyChain}
          progress={applyProgress}
          failed={applyFailed}
          polling={applyPoll.polling}
          elapsed={applyPoll.elapsed}
          starting={applyStarting}
          stopping={applyStopping}
          onRun={runApply}
          onStop={stopApplyChain}
          runLabel={previewComplete ? `Apply (${fmtInt(previewChain?.metadata?.cohort_count)} candidates)` : 'Apply'}
          runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running' || !previewComplete}
        />
        {!previewComplete && (
          <p className="text-xs text-muted-foreground">
            Apply is disabled until a Preview chain over the current cohort completes.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
